import React, { useMemo, useState } from 'react';
import { sortLevels } from '../../utils/levelSorter';
import { MENS_APPARATUS, WOMENS_APPARATUS } from '../../constants/competitionConstants';
import { decodeHtmlEntities } from '../../utils/textUtils';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Avatar,
  LinearProgress,
  useTheme,
  alpha,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Tabs,
  Tab,
  FormControl,
  Select,
  MenuItem
} from '@mui/material';
import {
  PeopleAlt,
  Groups,
  Event,
  PersonOff,
  TrendingUp,
  BarChart as ChartIcon,
  GridView as GridIcon,
  Assessment as AssessmentIcon,
  ShowChart as RangeIcon,
  Equalizer as HistogramIcon
} from '@mui/icons-material';

const StatCard = ({ title, value, subtitle, icon, color, percent, dataUi }) => {
  const theme = useTheme();

  return (
    <Card sx={{ height: '100%', position: 'relative', overflow: 'hidden' }} data-ui={dataUi}>
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          p: 2,
          opacity: 0.1,
          transform: 'translate(20%, -20%) scale(2.5)',
          color: color
        }}
      >
        {icon}
      </Box>
      
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
          <Avatar
            variant="rounded"
            sx={{
              bgcolor: alpha(color, 0.1),
              color: color,
              width: 48,
              height: 48,
              mr: 2
            }}
          >
            {icon}
          </Avatar>
          <Box>
            <Typography variant="overline" color="text.secondary" fontWeight={600} sx={{ lineHeight: 1.2, display: 'block' }}>
              {title}
            </Typography>
            <Typography variant="h4" fontWeight={700} sx={{ lineHeight: 1 }}>
              {value}
            </Typography>
          </Box>
        </Box>
        
        {percent !== undefined ? (
          <Box sx={{ mt: 2 }}>
             <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                Participation
              </Typography>
              <Typography variant="caption" fontWeight={600} color={color}>
                {percent}%
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={percent} 
              sx={{ 
                height: 6, 
                borderRadius: 3,
                bgcolor: alpha(color, 0.1),
                '& .MuiLinearProgress-bar': {
                  bgcolor: color,
                  borderRadius: 3
                }
              }} 
            />
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

const LevelHistogram = ({ data, maxCount, color }) => {
  return (
    <Box sx={{
      height: 300,
      display: 'flex',
      gap: 2,
      pt: 4,
      pb: 2,
      px: 1,
      overflowX: 'auto'
    }}>
      {data.map(([level, count]) => {
        const heightPercent = (count / maxCount) * 85;
        return (
          <Box
            key={level}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'flex-end',
              height: '100%',
              flex: 1,
              minWidth: 40
            }}
          >
            <Tooltip title={`${count} Athletes`} arrow placement="top">
              <Box
                sx={{
                  width: '100%',
                  height: `${Math.max(heightPercent, 1)}%`, // Ensure at least 1% height
                  bgcolor: color,
                  borderRadius: '4px 4px 0 0',
                  opacity: 0.8,
                  transition: 'all 0.2s',
                  position: 'relative',
                  '&:hover': {
                    opacity: 1,
                    transform: 'scaleY(1.02)',
                    transformOrigin: 'bottom'
                  }
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    position: 'absolute',
                    top: -20,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    fontWeight: 600,
                    color: 'text.primary'
                  }}
                >
                  {count}
                </Typography>
              </Box>
            </Tooltip>
            <Typography
              variant="caption"
              sx={{
                mt: 1,
                fontWeight: 500,
                color: 'text.secondary',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '100%',
                fontSize: '0.7rem'
              }}
            >
              {level}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

// Range Plot component - shows min/max/mean for each level
const ScoreRangePlot = ({ data, color }) => {
  const theme = useTheme();

  if (!data || data.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography color="text.secondary">No score data available</Typography>
      </Box>
    );
  }

  // Calculate dynamic scale from actual data with padding
  const allMins = data.map(d => d.min);
  const allMaxes = data.map(d => d.max);
  const dataMin = Math.min(...allMins);
  const dataMax = Math.max(...allMaxes);
  const range = dataMax - dataMin;
  const padding = Math.max(range * 0.2, 0.5); // At least 0.5 point padding
  const scaleMin = Math.max(0, Math.floor((dataMin - padding) * 2) / 2); // Round down to nearest 0.5
  const scaleMax = Math.ceil((dataMax + padding) * 2) / 2; // Round up to nearest 0.5
  const scaleRange = scaleMax - scaleMin;

  // Calculate percentile thresholds from all scores
  const allScores = data.flatMap(d => d.scores || []).sort((a, b) => a - b);
  const p80 = allScores.length > 0 ? allScores[Math.floor(allScores.length * 0.8)] : null;
  const p90 = allScores.length > 0 ? allScores[Math.floor(allScores.length * 0.9)] : null;
  const p80Percent = p80 !== null ? ((p80 - scaleMin) / scaleRange) * 100 : null;
  const p90Percent = p90 !== null ? ((p90 - scaleMin) / scaleRange) * 100 : null;

  // Generate tick marks
  const tickCount = 5;
  const tickStep = scaleRange / (tickCount - 1);
  const ticks = Array.from({ length: tickCount }, (_, i) => scaleMin + (i * tickStep));

  return (
    <Box sx={{ pt: 2, pb: 1, px: 1 }}>
      {/* Scale axis */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Box sx={{ width: 70, flexShrink: 0 }} />
        <Box sx={{ flex: 1, mx: 1, display: 'flex', justifyContent: 'space-between' }}>
          {ticks.map((tick, i) => (
            <Typography key={i} variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
              {tick.toFixed(1)}
            </Typography>
          ))}
        </Box>
        <Box sx={{ width: 60, flexShrink: 0 }} />
      </Box>

      {data.map(({ level, min, max, mean, stdDev, count, p25, p50, p75 }) => {
        const minPercent = ((min - scaleMin) / scaleRange) * 100;
        const maxPercent = ((max - scaleMin) / scaleRange) * 100;
        const meanPercent = ((mean - scaleMin) / scaleRange) * 100;
        const stdLowPercent = Math.max(0, ((mean - stdDev - scaleMin) / scaleRange) * 100);
        const stdHighPercent = Math.min(100, ((mean + stdDev - scaleMin) / scaleRange) * 100);
        const p25Percent = p25 ? ((p25 - scaleMin) / scaleRange) * 100 : null;
        const p50Percent = p50 ? ((p50 - scaleMin) / scaleRange) * 100 : null;
        const p75Percent = p75 ? ((p75 - scaleMin) / scaleRange) * 100 : null;

        return (
          <Box
            key={level}
            sx={{
              display: 'flex',
              alignItems: 'center',
              mb: 4,
              '&:last-child': { mb: 0 }
            }}
          >
            <Typography
              variant="caption"
              sx={{
                width: 70,
                flexShrink: 0,
                fontWeight: 600,
                color: 'text.secondary',
                fontSize: '0.75rem'
              }}
            >
              Lvl {level}
            </Typography>
            <Box sx={{ flex: 1, position: 'relative', height: 44, mx: 1 }}>
              {/* Top 20% zone (amber) */}
              {p80Percent !== null && p80Percent < 100 && (
                <Box sx={{
                  position: 'absolute',
                  left: `${Math.max(0, p80Percent)}%`,
                  right: p90Percent !== null ? `${100 - Math.min(100, p90Percent)}%` : 0,
                  top: 0,
                  bottom: 0,
                  bgcolor: alpha(theme.palette.warning.main, 0.1),
                  borderLeft: `1px dashed ${alpha(theme.palette.warning.main, 0.5)}`
                }} />
              )}
              {/* Top 10% zone (green) */}
              {p90Percent !== null && p90Percent < 100 && (
                <Box sx={{
                  position: 'absolute',
                  left: `${Math.max(0, p90Percent)}%`,
                  right: 0,
                  top: 0,
                  bottom: 0,
                  bgcolor: alpha(theme.palette.success.main, 0.1),
                  borderLeft: `1px dashed ${alpha(theme.palette.success.main, 0.5)}`
                }} />
              )}
              {/* Grid lines */}
              {ticks.map((tick, i) => (
                <Box
                  key={i}
                  sx={{
                    position: 'absolute',
                    left: `${(i / (tickCount - 1)) * 100}%`,
                    top: 0,
                    bottom: 0,
                    width: 1,
                    bgcolor: alpha(theme.palette.text.primary, 0.06)
                  }}
                />
              ))}
              {/* Background track */}
              <Box sx={{
                position: 'absolute',
                left: 0,
                right: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                height: 2,
                bgcolor: alpha(theme.palette.text.primary, 0.1),
                borderRadius: 1
              }} />

              {/* Standard deviation range (lighter) */}
              <Tooltip
                title={`±1 σ: ${(mean - stdDev).toFixed(2)} - ${(mean + stdDev).toFixed(2)}`}
                arrow
              >
                <Box sx={{
                  position: 'absolute',
                  left: `${stdLowPercent}%`,
                  width: `${stdHighPercent - stdLowPercent}%`,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  height: 8,
                  bgcolor: alpha(color, 0.3),
                  borderRadius: 1
                }} />
              </Tooltip>

              {/* Min-max range line */}
              <Tooltip
                title={`Range: ${min.toFixed(2)} - ${max.toFixed(2)}`}
                arrow
              >
                <Box sx={{
                  position: 'absolute',
                  left: `${minPercent}%`,
                  width: `${maxPercent - minPercent}%`,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  height: 4,
                  bgcolor: alpha(color, 0.6),
                  borderRadius: 1
                }} />
              </Tooltip>

              {/* Percentile markers */}
              {p25Percent !== null && (
                <Tooltip title={`P25: ${p25.toFixed(3)}`} arrow>
                  <Box sx={{
                    position: 'absolute',
                    left: `${p25Percent}%`,
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 2,
                    height: 14,
                    bgcolor: alpha(color, 0.8),
                    zIndex: 1
                  }} />
                </Tooltip>
              )}
              {p50Percent !== null && (
                <Tooltip title={`Median: ${p50.toFixed(3)}`} arrow>
                  <Box sx={{
                    position: 'absolute',
                    left: `${p50Percent}%`,
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 3,
                    height: 18,
                    bgcolor: color,
                    zIndex: 1
                  }} />
                </Tooltip>
              )}
              {p75Percent !== null && (
                <Tooltip title={`P75: ${p75.toFixed(3)}`} arrow>
                  <Box sx={{
                    position: 'absolute',
                    left: `${p75Percent}%`,
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 2,
                    height: 14,
                    bgcolor: alpha(color, 0.8),
                    zIndex: 1
                  }} />
                </Tooltip>
              )}

              {/* Min marker */}
              <Tooltip title={`Min: ${min.toFixed(2)}`} arrow>
                <Box sx={{
                  position: 'absolute',
                  left: `${minPercent}%`,
                  top: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: 8,
                  height: 8,
                  bgcolor: 'background.paper',
                  border: `2px solid ${color}`,
                  borderRadius: '50%',
                  zIndex: 1
                }} />
              </Tooltip>

              {/* Max marker */}
              <Tooltip title={`Max: ${max.toFixed(2)}`} arrow>
                <Box sx={{
                  position: 'absolute',
                  left: `${maxPercent}%`,
                  top: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: 8,
                  height: 8,
                  bgcolor: 'background.paper',
                  border: `2px solid ${color}`,
                  borderRadius: '50%',
                  zIndex: 1
                }} />
              </Tooltip>

              {/* Mean marker (diamond) */}
              <Tooltip title={`Mean: ${mean.toFixed(3)} (n=${count})`} arrow>
                <Box sx={{
                  position: 'absolute',
                  left: `${meanPercent}%`,
                  top: '50%',
                  transform: 'translate(-50%, -50%) rotate(45deg)',
                  width: 10,
                  height: 10,
                  bgcolor: color,
                  zIndex: 2
                }} />
              </Tooltip>
            </Box>
            <Box
              sx={{
                width: 60,
                flexShrink: 0,
                textAlign: 'right'
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: color,
                  fontSize: '0.8rem'
                }}
              >
                {mean.toFixed(3)}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  color: 'text.secondary',
                  fontSize: '0.6rem'
                }}
              >
                n={count}
              </Typography>
            </Box>
          </Box>
        );
      })}

      {/* Legend */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        flexWrap: 'wrap',
        gap: 2,
        mt: 3,
        pt: 2,
        borderTop: 1,
        borderColor: 'divider'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 8, height: 8, bgcolor: 'background.paper', border: `2px solid ${color}`, borderRadius: '50%' }} />
          <Typography variant="caption" color="text.secondary">Min/Max</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 10, height: 10, bgcolor: color, transform: 'rotate(45deg)' }} />
          <Typography variant="caption" color="text.secondary">Mean</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 3, height: 14, bgcolor: color }} />
          <Typography variant="caption" color="text.secondary">Median</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 2, height: 10, bgcolor: alpha(color, 0.8) }} />
          <Typography variant="caption" color="text.secondary">P25/P75</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 20, height: 8, bgcolor: alpha(color, 0.3), borderRadius: 1 }} />
          <Typography variant="caption" color="text.secondary">±1σ</Typography>
        </Box>
      </Box>

      {/* Percentile zones legend */}
      {p80 !== null && p90 !== null && (
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          flexWrap: 'wrap',
          gap: 3,
          mt: 1.5,
          pt: 1,
          borderTop: 1,
          borderColor: 'divider'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 16, height: 12, bgcolor: alpha(theme.palette.success.main, 0.15), border: `1px dashed ${alpha(theme.palette.success.main, 0.5)}`, borderRadius: 0.5 }} />
            <Typography variant="caption" color="text.secondary">Top 10% (≥{p90.toFixed(3)})</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 16, height: 12, bgcolor: alpha(theme.palette.warning.main, 0.15), border: `1px dashed ${alpha(theme.palette.warning.main, 0.5)}`, borderRadius: 0.5 }} />
            <Typography variant="caption" color="text.secondary">Top 20% (≥{p80.toFixed(3)})</Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

// Score Histogram component - shows distribution of scores
const ScoreDistributionHistogram = ({ data, color, binSize = 1 }) => {
  const theme = useTheme();

  if (!data || data.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography color="text.secondary">No score data available</Typography>
      </Box>
    );
  }

  // Flatten all scores and create histogram bins
  const allScores = data.flatMap(d => d.scores || []);
  if (allScores.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography color="text.secondary">No scores recorded yet</Typography>
      </Box>
    );
  }

  // Calculate percentile thresholds for color coding
  const sortedScores = [...allScores].sort((a, b) => a - b);
  const p80 = sortedScores[Math.floor(sortedScores.length * 0.8)]; // Top 20%
  const p90 = sortedScores[Math.floor(sortedScores.length * 0.9)]; // Top 10%

  const minScore = Math.floor(Math.min(...allScores));
  const maxScore = Math.ceil(Math.max(...allScores));

  // Create bins
  const bins = [];
  for (let start = minScore; start < maxScore; start += binSize) {
    const end = start + binSize;
    const count = allScores.filter(s => s >= start && s < end).length;
    bins.push({ start, end, count });
  }

  const maxCount = Math.max(...bins.map(b => b.count), 1);

  return (
    <Box sx={{
      height: 280,
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Histogram bars */}
      <Box sx={{
        flex: 1,
        display: 'flex',
        alignItems: 'flex-end',
        gap: 0.5,
        px: 1,
        pb: 1
      }}>
        {bins.map((bin, index) => {
          const heightPercent = (bin.count / maxCount) * 100;
          // Determine color based on bin midpoint relative to percentiles
          const binMid = (bin.start + bin.end) / 2;
          let barColor = color;
          let percentileLabel = '';
          if (binMid >= p90) {
            barColor = theme.palette.success.main; // Top 10% - green
            percentileLabel = ' (Top 10%)';
          } else if (binMid >= p80) {
            barColor = theme.palette.warning.main; // Top 20% - amber/gold
            percentileLabel = ' (Top 20%)';
          }
          return (
            <Tooltip
              key={index}
              title={`${bin.start.toFixed(3)}-${bin.end.toFixed(3)}: ${bin.count} scores${percentileLabel}`}
              arrow
              placement="top"
            >
              <Box
                sx={{
                  flex: 1,
                  height: `${Math.max(heightPercent, 2)}%`,
                  bgcolor: barColor,
                  opacity: 0.8,
                  borderRadius: '2px 2px 0 0',
                  transition: 'all 0.2s',
                  minWidth: 4,
                  '&:hover': {
                    opacity: 1,
                    transform: 'scaleY(1.05)',
                    transformOrigin: 'bottom'
                  }
                }}
              />
            </Tooltip>
          );
        })}
      </Box>

      {/* X-axis labels */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        px: 1,
        pt: 0.5,
        borderTop: 1,
        borderColor: 'divider'
      }}>
        <Typography variant="caption" color="text.secondary">{minScore}</Typography>
        <Typography variant="caption" color="text.secondary">Score Distribution</Typography>
        <Typography variant="caption" color="text.secondary">{maxScore}</Typography>
      </Box>

      {/* Summary stats */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        gap: 4,
        mt: 2,
        pt: 1,
        borderTop: 1,
        borderColor: 'divider'
      }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" fontWeight={700} color={color}>
            {(allScores.reduce((a, b) => a + b, 0) / allScores.length).toFixed(3)}
          </Typography>
          <Typography variant="caption" color="text.secondary">Mean</Typography>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" fontWeight={700} color={color}>
            {allScores.length}
          </Typography>
          <Typography variant="caption" color="text.secondary">Scores</Typography>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" fontWeight={700} color={color}>
            {(maxScore - minScore).toFixed(1)}
          </Typography>
          <Typography variant="caption" color="text.secondary">Range</Typography>
        </Box>
      </Box>

      {/* Percentile legend */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        gap: 3,
        mt: 1.5,
        pt: 1,
        borderTop: 1,
        borderColor: 'divider'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 12, height: 12, bgcolor: theme.palette.success.main, borderRadius: 0.5 }} />
          <Typography variant="caption" color="text.secondary">Top 10% (≥{p90.toFixed(3)})</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 12, height: 12, bgcolor: theme.palette.warning.main, borderRadius: 0.5 }} />
          <Typography variant="caption" color="text.secondary">Top 20% (≥{p80.toFixed(3)})</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 12, height: 12, bgcolor: color, borderRadius: 0.5 }} />
          <Typography variant="caption" color="text.secondary">Below Top 20%</Typography>
        </Box>
      </Box>
    </Box>
  );
};

const CompetitionStats = ({ gymnasts = [], teams = [], sessions = [], competitionColors }) => {
  const theme = useTheme();
  const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'histogram' for levels
  const [teamViewMode, setTeamViewMode] = useState('cards'); // 'cards' or 'histogram' for teams
  const [scoreViewMode, setScoreViewMode] = useState('range'); // 'range' or 'histogram' for scores
  const [scoreTab, setScoreTab] = useState(0); // 0 = AA, 1+ = apparatus indices
  const [selectedScoreLevel, setSelectedScoreLevel] = useState('all'); // 'all' or specific level

  const totalAthletes = gymnasts.length;
  const totalTeams = teams.length;
  const totalSessions = sessions.length;
  const totalScratches = gymnasts.filter(g => g.scratched).length;
  const activeAthletes = gymnasts.filter(g => !g.scratched).length;
  const participationRate = totalAthletes > 0 ? Math.round((activeAthletes / totalAthletes) * 100) : 0;

  // Determine gender and apparatus based on sessions
  const gender = useMemo(() => {
    if (!sessions || sessions.length === 0) return 'F';
    const maleCount = sessions.filter(s => s.gender === 'M').length;
    const femaleCount = sessions.filter(s => s.gender === 'F').length;
    return maleCount > femaleCount ? 'M' : 'F';
  }, [sessions]);

  const apparatus = useMemo(() => {
    return gender === 'M' ? MENS_APPARATUS : WOMENS_APPARATUS;
  }, [gender]);

  // Helper function to calculate percentile
  const percentile = (arr, p) => {
    const sorted = [...arr].sort((a, b) => a - b);
    const index = (p / 100) * (sorted.length - 1);
    const lower = Math.floor(index);
    const upper = Math.ceil(index);
    if (lower === upper) return sorted[lower];
    return sorted[lower] + (sorted[upper] - sorted[lower]) * (index - lower);
  };

  // Helper function to calculate statistics
  const calculateStats = (scores) => {
    if (!scores || scores.length === 0) return null;
    const validScores = scores.filter(s => s > 0);
    if (validScores.length === 0) return null;

    const sum = validScores.reduce((a, b) => a + b, 0);
    const mean = sum / validScores.length;
    const min = Math.min(...validScores);
    const max = Math.max(...validScores);
    const variance = validScores.reduce((acc, s) => acc + Math.pow(s - mean, 2), 0) / validScores.length;
    const stdDev = Math.sqrt(variance);
    const p25 = percentile(validScores, 25);
    const p50 = percentile(validScores, 50); // median
    const p75 = percentile(validScores, 75);

    return { mean, min, max, stdDev, count: validScores.length, scores: validScores, p25, p50, p75 };
  };

  // Calculate AA score statistics by level
  const aaScoreStatsByLevel = useMemo(() => {
    if (!gymnasts.length) return [];

    const activeGymnasts = gymnasts.filter(g => !g.scratched && g.aa > 0);
    const levelGroups = {};

    activeGymnasts.forEach(g => {
      const level = g.level || 'Unassigned';
      if (!levelGroups[level]) levelGroups[level] = [];
      levelGroups[level].push(g.aa);
    });

    return Object.entries(levelGroups)
      .map(([level, scores]) => {
        const stats = calculateStats(scores);
        if (!stats) return null;
        return { level, ...stats };
      })
      .filter(Boolean)
      .sort((a, b) => sortLevels(a.level, b.level));
  }, [gymnasts]);

  // Calculate apparatus score statistics by level
  const apparatusScoreStatsByLevel = useMemo(() => {
    if (!gymnasts.length || !apparatus.length) return {};

    const activeGymnasts = gymnasts.filter(g => !g.scratched);
    const result = {};

    apparatus.forEach((app, index) => {
      const eventKey = `E${index + 1}`;
      const levelGroups = {};

      activeGymnasts.forEach(g => {
        const level = g.level || 'Unassigned';
        const score = g.scores?.[eventKey]?.final;
        if (score && score > 0) {
          if (!levelGroups[level]) levelGroups[level] = [];
          levelGroups[level].push(score);
        }
      });

      result[app.id] = Object.entries(levelGroups)
        .map(([level, scores]) => {
          const stats = calculateStats(scores);
          if (!stats) return null;
          return { level, ...stats };
        })
        .filter(Boolean)
        .sort((a, b) => sortLevels(a.level, b.level));
    });

    return result;
  }, [gymnasts, apparatus]);

  // Get available levels for score breakdown filter
  const availableScoreLevels = useMemo(() => {
    const levels = new Set();
    aaScoreStatsByLevel.forEach(s => levels.add(s.level));
    Object.values(apparatusScoreStatsByLevel).forEach(stats => {
      stats.forEach(s => levels.add(s.level));
    });
    return Array.from(levels).sort((a, b) => sortLevels(a, b));
  }, [aaScoreStatsByLevel, apparatusScoreStatsByLevel]);

  // Get current score stats based on selected tab and level filter
  const currentScoreStats = useMemo(() => {
    let stats;
    if (scoreTab === 0) {
      stats = aaScoreStatsByLevel;
    } else {
      const appId = apparatus[scoreTab - 1]?.id;
      stats = apparatusScoreStatsByLevel[appId] || [];
    }

    // Filter by selected level if not 'all'
    if (selectedScoreLevel !== 'all') {
      stats = stats.filter(s => s.level === selectedScoreLevel);
    }

    return stats;
  }, [scoreTab, aaScoreStatsByLevel, apparatusScoreStatsByLevel, apparatus, selectedScoreLevel]);

  const handleScoreViewChange = (event, nextView) => {
    if (nextView !== null) {
      setScoreViewMode(nextView);
    }
  };

  const handleScoreTabChange = (event, newValue) => {
    setScoreTab(newValue);
  };

  const handleScoreLevelChange = (event) => {
    setSelectedScoreLevel(event.target.value);
  };

  // Calculate active athletes per level
  const athletesByLevel = useMemo(() => {
    if (!gymnasts.length) return {};
    
    return gymnasts
      .filter(g => !g.scratched)
      .reduce((acc, gymnast) => {
        const level = gymnast.level || 'Unassigned';
        acc[level] = (acc[level] || 0) + 1;
        return acc;
      }, {});
  }, [gymnasts]);

  const sortedLevels = useMemo(() => {
    return Object.entries(athletesByLevel).sort(([a], [b]) => sortLevels(a, b));
  }, [athletesByLevel]);

  const maxLevelCount = Math.max(...Object.values(athletesByLevel), 0);

  // Calculate active athletes per team
  const athletesByTeam = useMemo(() => {
    if (!gymnasts.length) return [];

    const counts = gymnasts
      .filter(g => !g.scratched)
      .reduce((acc, gymnast) => {
        let teamName = 'Unattached';

        if (gymnast.teamId && teams.length > 0) {
          const team = teams.find(t => String(t.id) === String(gymnast.teamId));
          if (team) {
            teamName = decodeHtmlEntities(team.shortName || team.name) || 'Unattached';
          }
        }

        if (teamName === 'Unattached') {
          teamName = decodeHtmlEntities(gymnast.club || gymnast.team) || 'Unattached';
        }

        acc[teamName] = (acc[teamName] || 0) + 1;
        return acc;
      }, {});

    return Object.entries(counts).sort(([,a], [,b]) => b - a);
  }, [gymnasts, teams]);

  const maxTeamCount = athletesByTeam.length > 0 ? athletesByTeam[0][1] : 0;

  const handleViewChange = (event, nextView) => {
    if (nextView !== null) {
      setViewMode(nextView);
    }
  };

  const handleTeamViewChange = (event, nextView) => {
    if (nextView !== null) {
      setTeamViewMode(nextView);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }} data-ui="statistics-content">
      {/* Key Metrics */}
      <Box
        data-ui="statistics-key-metrics"
        sx={{
          display: 'grid',
          gap: 3,
          gridTemplateColumns: {
            xs: 'repeat(auto-fit, minmax(220px, 1fr))',
            sm: 'repeat(auto-fit, minmax(240px, 1fr))',
          },
          alignItems: 'stretch',
        }}
      >
        <StatCard
          title="Active Athletes"
          value={activeAthletes}
          percent={participationRate}
          color={competitionColors.primary}
          icon={<PeopleAlt />}
          dataUi="statistics-card-active-athletes"
        />
        <StatCard
          title="Total Teams"
          value={totalTeams}
          subtitle={`${totalTeams} participating clubs`}
          color={competitionColors.secondary}
          icon={<Groups />}
          dataUi="statistics-card-total-teams"
        />
        <StatCard
          title="Sessions"
          value={totalSessions}
          subtitle="Scheduled sessions"
          color={theme.palette.success.main}
          icon={<Event />}
          dataUi="statistics-card-sessions"
        />
        <StatCard
          title="Scratches"
          value={totalScratches}
          subtitle="Withdrawn athletes"
          color={theme.palette.error.main}
          icon={<PersonOff />}
          dataUi="statistics-card-scratches"
        />
      </Box>

      {/* Level Breakdown */}
      <Card data-ui="statistics-level-breakdown-card">
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: alpha(competitionColors.primary, 0.1), color: competitionColors.primary }}>
                <TrendingUp />
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight={600}>
                  Level Breakdown
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Distribution of active athletes across {sortedLevels.length} levels
                </Typography>
              </Box>
            </Box>

            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewChange}
              size="small"
              aria-label="view mode"
              data-ui="statistics-level-view-toggle"
            >
              <ToggleButton value="cards" aria-label="card view" data-ui="statistics-level-grid-view">
                <Tooltip title="Grid View">
                  <GridIcon fontSize="small" />
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="histogram" aria-label="histogram view" data-ui="statistics-level-chart-view">
                <Tooltip title="Chart View">
                  <ChartIcon fontSize="small" />
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {sortedLevels.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'background.default', borderRadius: 2 }}>
              <Typography color="text.secondary">No athlete data available</Typography>
            </Box>
          ) : (
            <Box>
              {viewMode === 'histogram' ? (
                <LevelHistogram
                  data={sortedLevels}
                  maxCount={maxLevelCount}
                  color={competitionColors.primary}
                />
              ) : (
                <Box
                  data-ui="statistics-level-grid"
                  sx={{
                    display: 'grid',
                    gap: 2,
                    gridTemplateColumns: {
                      xs: 'repeat(auto-fit, minmax(220px, 1fr))',
                      sm: 'repeat(auto-fit, minmax(240px, 1fr))',
                      md: 'repeat(3, 1fr)',
                    },
                    alignItems: 'stretch',
                  }}
                >
                  {sortedLevels.map(([level, count]) => (
                    <Box
                      key={level}
                      sx={{
                        p: 2,
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 2,
                        height: '100%',
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: competitionColors.primary,
                          bgcolor: alpha(competitionColors.primary, 0.02),
                          transform: 'translateY(-2px)',
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1 }}>
                        <Typography variant="subtitle1" fontWeight={600}>
                          Level {level}
                        </Typography>
                        <Typography variant="h5" fontWeight={700} color={competitionColors.primary}>
                          {count}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={(count / maxLevelCount) * 100}
                          sx={{
                            flexGrow: 1,
                            height: 6,
                            borderRadius: 3,
                            bgcolor: 'action.hover',
                            '& .MuiLinearProgress-bar': {
                              bgcolor: competitionColors.primary,
                              borderRadius: 3,
                            },
                          }}
                        />
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Score Breakdown */}
      <Card data-ui="statistics-score-breakdown-card">
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), color: theme.palette.info.main }}>
                <AssessmentIcon />
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight={600}>
                  Score Breakdown
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Score distribution by level (range, mean, std dev)
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <Select
                  value={selectedScoreLevel}
                  onChange={handleScoreLevelChange}
                  displayEmpty
                  sx={{ fontSize: '0.875rem' }}
                  data-ui="statistics-score-level-select"
                >
                  <MenuItem value="all">All Levels</MenuItem>
                  {availableScoreLevels.map(level => (
                    <MenuItem key={level} value={level}>Lvl {level}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <ToggleButtonGroup
                value={scoreViewMode}
                exclusive
                onChange={handleScoreViewChange}
                size="small"
                aria-label="score view mode"
                data-ui="statistics-score-view-toggle"
              >
                <ToggleButton value="range" aria-label="range view" data-ui="statistics-score-range-view">
                  <Tooltip title="Range Plot">
                    <RangeIcon fontSize="small" />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="histogram" aria-label="histogram view" data-ui="statistics-score-histogram-view">
                  <Tooltip title="Histogram">
                    <HistogramIcon fontSize="small" />
                  </Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>

          {/* Tabs for AA and Apparatus */}
          <Tabs
            value={scoreTab}
            onChange={handleScoreTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              mb: 2,
              '& .MuiTab-root': {
                minWidth: 'auto',
                px: 2,
                py: 1,
                fontWeight: 500,
                textTransform: 'none',
              }
            }}
            data-ui="statistics-score-tabs"
          >
            <Tab label="All-Around" data-ui="statistics-score-tab-aa" />
            {apparatus.map((app) => (
              <Tab key={app.id} label={app.abbrev} data-ui={`statistics-score-tab-${app.id.toLowerCase()}`} />
            ))}
          </Tabs>

          {/* Score visualization */}
          {currentScoreStats.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'background.default', borderRadius: 2 }}>
              <Typography color="text.secondary">
                No {scoreTab === 0 ? 'All-Around' : apparatus[scoreTab - 1]?.name} scores recorded yet
              </Typography>
            </Box>
          ) : (
            <Box>
              {scoreViewMode === 'range' ? (
                <ScoreRangePlot
                  data={currentScoreStats}
                  color={theme.palette.info.main}
                />
              ) : (
                <ScoreDistributionHistogram
                  data={currentScoreStats}
                  color={theme.palette.info.main}
                  binSize={scoreTab === 0 ? 0.25 : 0.1}
                />
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Team Breakdown */}
      <Card data-ui="statistics-team-breakdown-card">
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: alpha(competitionColors.secondary, 0.1), color: competitionColors.secondary }}>
                <Groups />
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight={600}>
                  Team Participation
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {athletesByTeam.length} teams by athlete count
                </Typography>
              </Box>
            </Box>

            <ToggleButtonGroup
              value={teamViewMode}
              exclusive
              onChange={handleTeamViewChange}
              size="small"
              aria-label="team view mode"
              data-ui="statistics-team-view-toggle"
            >
              <ToggleButton value="cards" aria-label="card view" data-ui="statistics-team-grid-view">
                <Tooltip title="Grid View">
                  <GridIcon fontSize="small" />
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="histogram" aria-label="histogram view" data-ui="statistics-team-chart-view">
                <Tooltip title="Chart View">
                  <ChartIcon fontSize="small" />
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {athletesByTeam.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'background.default', borderRadius: 2 }}>
              <Typography color="text.secondary">No team data available</Typography>
            </Box>
          ) : (
            <Box>
              {teamViewMode === 'histogram' ? (
                <LevelHistogram
                  data={athletesByTeam}
                  maxCount={maxTeamCount}
                  color={competitionColors.secondary}
                />
              ) : (
                <Box
                  data-ui="statistics-team-grid"
                  sx={{
                    display: 'grid',
                    gap: 2,
                    gridTemplateColumns: {
                      xs: 'repeat(auto-fit, minmax(220px, 1fr))',
                      sm: 'repeat(auto-fit, minmax(240px, 1fr))',
                      md: 'repeat(4, 1fr)',
                    },
                    alignItems: 'stretch',
                  }}
                >
                  {athletesByTeam.map(([teamName, count]) => (
                    <Box
                      key={teamName}
                      sx={{
                        p: 2,
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 2,
                        height: '100%',
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: competitionColors.secondary,
                          bgcolor: alpha(competitionColors.secondary, 0.02),
                          transform: 'translateY(-2px)',
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Typography 
                          variant="subtitle2" 
                          fontWeight={600} 
                          sx={{ 
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: '80%'
                          }}
                          title={teamName}
                        >
                          {teamName}
                        </Typography>
                        <Typography variant="h6" fontWeight={700} color={competitionColors.secondary} sx={{ lineHeight: 1 }}>
                          {count}
                        </Typography>
                      </Box>
                      
                      <LinearProgress
                        variant="determinate"
                        value={(count / maxTeamCount) * 100}
                        sx={{
                          height: 6,
                          borderRadius: 3,
                          bgcolor: 'action.hover',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: competitionColors.secondary,
                            borderRadius: 3
                          }
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default CompetitionStats;
